import cn from 'classnames/bind';
import React, { CSSProperties, FC, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStorage } from '@funtech-int/web-toolkit';

import { Icon, IconSize, IconType } from '../Icon';
import { Option, OptionList, OptionListHeight } from '../OptionList';
import { useSeamlessResizeControllerContext } from '../SeamlessResizeController';
import { SubtitlesRatio, subtitlesRatioValues } from '../Subtitles/Subtitles.types';

import { useAutoQualityOption } from './hooks/use-auto-quality-options';
import {
    animationDuration,
    subtitlesRatioLocalStorage,
    getSubtitlesSizeOptions,
} from './Settings.config';
import { PlaybackRate, SettingsProps, SettingsScreen } from './Settings.types';
import {
    getCurrentSubtitlesSizeOption,
    getQualityOptions,
    getSettingsOptions,
    rangeByStep,
} from './helpers';
import styles from './Settings.module.css';

const cx = cn.bind(styles);

export const Settings: FC<SettingsProps> = ({
    isSubtitlesSizeForced,
    playerContentType,
    subtitlesRatio = subtitlesRatioValues.NORMAL,
    setSubtitlesRatio,
    videoTracks,
    currentVideoTrack,
    currentRate,
    playbackRateRange,
    handleQualitySelect,
    handleRateSelect,
}) => {
    const intl = useIntl();

    const storage = useStorage();

    const { toggleResize } = useSeamlessResizeControllerContext();
    const [listNode, setListNode] = useState<HTMLDivElement | null>(null);
    const [containerStyle, setContainerStyle] = useState<CSSProperties>({});

    let startCurrentScreen = SettingsScreen.LIST;
    if (isSubtitlesSizeForced) {
        startCurrentScreen = SettingsScreen.SUBTITLES_SIZE;
    }

    const [currentScreen, setCurrentScreen] = useState<SettingsScreen>(startCurrentScreen);
    const currentSubtitlesSize = getCurrentSubtitlesSizeOption(intl, subtitlesRatio);
    const isListScreen = currentScreen === SettingsScreen.LIST;
    const isQualityScreen = currentScreen === SettingsScreen.QUALITY;
    const isRateScreen = currentScreen === SettingsScreen.RATE;
    const isSubtitlesSizeScreen = currentScreen === SettingsScreen.SUBTITLES_SIZE;
    const isScreenVisible = isQualityScreen || isRateScreen || isSubtitlesSizeScreen;

    let currentQuality = currentVideoTrack as Option<string>;
    const autoQualityOption = useAutoQualityOption();

    if (currentVideoTrack && currentVideoTrack.auto) {
        autoQualityOption.note = currentVideoTrack.name;
        currentQuality = autoQualityOption;
    } else autoQualityOption.note = '';

    const qualityOptions = useMemo(
        () => getQualityOptions(videoTracks, autoQualityOption),
        [autoQualityOption, videoTracks]
    );

    const rates: PlaybackRate[] | undefined = useMemo(() => {
        if (!playbackRateRange) {
            return undefined;
        }

        const { min, max } = playbackRateRange;

        return rangeByStep(min, max);
    }, [playbackRateRange]);

    const rateOptions: Option<PlaybackRate>[] | undefined = rates?.map((rate) => {
        const name =
            rate === 1
                ? intl.formatMessage({
                      id: 'components.PlayerSkin.960b44c579bc2f6818d2daaf9e4c16f0',

                      defaultMessage: 'Normal',
                      description: 'Текст для обычная скорости воспроизведения видео',
                  })
                : String(rate);

        return {
            name,
            value: rate,
        };
    });

    const currentRateOption = useMemo(
        () => rateOptions?.find(({ value }) => value === currentRate) || null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentRate]
    );

    const handleScreenSelect = useCallback((screen: Option<SettingsScreen>) => {
        setCurrentScreen(screen.value);
    }, []);

    const handleBackButtonClick = useCallback(() => {
        setCurrentScreen(SettingsScreen.LIST);
    }, []);

    const handleSubtitlesSizeSelect = useCallback(
        (subtitlesSizeOption: Option<SubtitlesRatio>) => {
            setSubtitlesRatio?.(subtitlesSizeOption.value);
            storage.local.set(subtitlesRatioLocalStorage, subtitlesSizeOption.value.toString());
        },
        [setSubtitlesRatio, storage]
    );

    useLayoutEffect(() => {
        toggleResize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScreen]);

    useLayoutEffect(() => {
        if (!listNode) {
            return;
        }

        if (isListScreen) {
            const { height } = listNode.getBoundingClientRect();
            setContainerStyle({
                height,
            });
        } else {
            setContainerStyle({});
        }
    }, [listNode, isListScreen]);

    const settingsOptions = useMemo(
        () =>
            getSettingsOptions({
                intl,
                currentQuality,
                currentRate: currentRateOption,
                currentSubtitlesSize,
                contentType: playerContentType || null,
                iconClassName: cx('icon-settings'),
            }),
        [intl, currentQuality, currentRateOption, currentSubtitlesSize, playerContentType]
    );

    const subtitlesSizeOptions = useMemo(() => getSubtitlesSizeOptions(intl), [intl]);

    return (
        <div className={cx('root')}>
            <div
                style={containerStyle}
                className={cx('container', {
                    container_screen: isScreenVisible,
                })}
            >
                <div ref={setListNode} className={cx('list')} aria-hidden={isScreenVisible}>
                    <OptionList
                        onSelect={handleScreenSelect}
                        value={null}
                        options={settingsOptions}
                    />
                </div>
                <div className={cx('screen')}>
                    <CSSTransition
                        in={isScreenVisible}
                        timeout={animationDuration}
                        mountOnEnter
                        unmountOnExit
                    >
                        <button onClick={handleBackButtonClick} className={cx('back-button')}>
                            <Icon
                                className={cx('icon')}
                                position="absolute"
                                type={IconType.BackArrow}
                                size={IconSize.Option}
                            />
                            {(isQualityScreen && (
                                <FormattedMessage
                                    id="components.PlayerSkin.571094bb27864b600d8e6b561a137a55"
                                    defaultMessage="Quality"
                                    description="Кнопка для выбора качества видео"
                                />
                            )) ||
                                (isRateScreen && (
                                    <FormattedMessage
                                        id="components.PlayerSkin.44877c6aa8e93fa5a91c9361211464fb"
                                        defaultMessage="Speed"
                                        description="Кнопка для выбора скорости воспроизведения"
                                    />
                                )) ||
                                (isSubtitlesSizeScreen && (
                                    <FormattedMessage
                                        id="components.PlayerSkin.3a9dd5e1d7c003ba046106646aa40aa4"
                                        defaultMessage="Subtitle size"
                                        description="Кнопка для выбора размера субтитров"
                                    />
                                ))}
                        </button>
                    </CSSTransition>
                    {currentSubtitlesSize && (
                        <CSSTransition
                            in={isSubtitlesSizeScreen}
                            timeout={animationDuration}
                            mountOnEnter
                            unmountOnExit
                        >
                            <OptionList
                                onSelect={handleSubtitlesSizeSelect}
                                value={currentSubtitlesSize}
                                options={subtitlesSizeOptions}
                                heightType={OptionListHeight.Hold}
                            />
                        </CSSTransition>
                    )}
                    <CSSTransition
                        in={isQualityScreen}
                        timeout={animationDuration}
                        mountOnEnter
                        unmountOnExit
                    >
                        <OptionList
                            onSelect={handleQualitySelect}
                            value={currentQuality}
                            options={qualityOptions}
                        />
                    </CSSTransition>
                    {rateOptions?.length && (
                        <CSSTransition
                            in={isRateScreen}
                            timeout={animationDuration}
                            mountOnEnter
                            unmountOnExit
                        >
                            <OptionList
                                onSelect={handleRateSelect}
                                value={currentRateOption}
                                options={rateOptions}
                            />
                        </CSSTransition>
                    )}
                </div>
            </div>
        </div>
    );
};
